// PlaneVisualizer — dev-only visualisation of every ARCore-detected
// plane. Draws a coloured LineLoop around each plane's polygon boundary,
// updating position + orientation every time the WebXR driver publishes
// a new snapshot.
//
// Not part of the wheel renderer — this is a standalone diagnostic
// layer wired up from main.ts when planeMode is enabled. Keeps
// WebXR-specific plane concepts out of WebXrWheelRenderer.

import * as THREE from 'three'
import type {
  DetectedPlaneInfo,
  DetectedPlanesStore,
} from '@/ar/webxr-driver'

interface PlaneViz {
  group: THREE.Group
  lineLoop: THREE.LineLoop
  lastPolygonHash: string
}

export class PlaneVisualizer {
  private readonly scene: THREE.Scene
  private readonly planes: DetectedPlanesStore
  private readonly meshes = new Map<string, PlaneViz>()
  private readonly unsubscribe: () => void

  constructor(scene: THREE.Scene, planes: DetectedPlanesStore) {
    this.scene = scene
    this.planes = planes
    this.sync()
    this.unsubscribe = planes.subscribe(() => this.sync())
  }

  private sync(): void {
    const snapshot = this.planes.getState()
    const seen = new Set<string>()
    for (const p of snapshot) {
      seen.add(p.id)
      const existing = this.meshes.get(p.id)
      if (existing == null) {
        const viz = this.createViz(p)
        this.scene.add(viz.group)
        this.meshes.set(p.id, viz)
      } else {
        this.updateViz(existing, p)
      }
    }
    // Prune planes that ARCore has dropped (rare — but its Set may lose
    // entries as it merges planes or loses confidence).
    for (const [id, viz] of Array.from(this.meshes.entries())) {
      if (!seen.has(id)) {
        this.scene.remove(viz.group)
        viz.lineLoop.geometry.dispose()
        ;(viz.lineLoop.material as THREE.Material).dispose()
        this.meshes.delete(id)
      }
    }
  }

  private createViz(p: DetectedPlaneInfo): PlaneViz {
    const group = new THREE.Group()
    group.name = 'plane-viz-' + p.id

    const material = new THREE.LineBasicMaterial({
      color: colorFor(p.orientation),
      linewidth: 2,
      depthTest: false, // always visible even through real-world occluders
      transparent: true,
      opacity: 0.85,
    })
    const geometry = new THREE.BufferGeometry()
    const lineLoop = new THREE.LineLoop(geometry, material)
    lineLoop.name = 'plane-viz-loop-' + p.id
    lineLoop.renderOrder = 999
    group.add(lineLoop)

    const viz: PlaneViz = { group, lineLoop, lastPolygonHash: '' }
    this.updateViz(viz, p)
    return viz
  }

  private updateViz(viz: PlaneViz, p: DetectedPlaneInfo): void {
    viz.group.position.set(p.position.x, p.position.y, p.position.z)
    viz.group.quaternion.set(
      p.quaternion.x,
      p.quaternion.y,
      p.quaternion.z,
      p.quaternion.w,
    )
    // Refresh geometry only when the polygon actually changed. ARCore
    // often grows / re-shapes a plane a few times per second while it
    // consolidates; regenerating each frame would be wasteful.
    const hash = polygonHash(p.polygon)
    if (hash === viz.lastPolygonHash) return
    viz.lastPolygonHash = hash
    const positions = new Float32Array(p.polygon.length * 3)
    for (let i = 0; i < p.polygon.length; i++) {
      // Polygon vertices are Y=0 in plane-local space.
      positions[i * 3 + 0] = p.polygon[i].x
      positions[i * 3 + 1] = 0
      positions[i * 3 + 2] = p.polygon[i].z
    }
    viz.lineLoop.geometry.setAttribute(
      'position',
      new THREE.BufferAttribute(positions, 3),
    )
    viz.lineLoop.geometry.computeBoundingSphere()
  }

  dispose(): void {
    this.unsubscribe()
    for (const viz of this.meshes.values()) {
      this.scene.remove(viz.group)
      viz.lineLoop.geometry.dispose()
      ;(viz.lineLoop.material as THREE.Material).dispose()
    }
    this.meshes.clear()
  }
}

// Bright green for vertical (walls / doors / windows), yellow for
// horizontal (floors / tables / ceilings) — quick eyeball distinction
// in the AR view. Values chosen for visibility against typical wall /
// carpet / wood colours.
function colorFor(orientation: 'horizontal' | 'vertical'): number {
  return orientation === 'vertical' ? 0x00ff88 : 0xffe066
}

function polygonHash(polygon: ReadonlyArray<{ x: number; z: number }>): string {
  // Length + a few rounded vertices as a cheap change-detection key.
  const summary = polygon
    .slice(0, 6)
    .map((p) => p.x.toFixed(3) + ',' + p.z.toFixed(3))
    .join('|')
  return polygon.length + ':' + summary
}
